<template>
  <div>
    <input
      ref="fileInput"
      type="file"
      multiple
      accept=".doc, .csv, .xlsx, .pdf, image/*, video/*"
      class="hidden"
      @change="fileSected"
    >
    <div class="flex flex-wrap bg-slate-0 text-slate-700 p-[1.125rem] gap-2 border border-solid border-slate-200 border-b-0 rounded-tr-lg rounded-tl-lg">
      <BaseButton
        variant="primary"
        text="Choose"
        icon-class="i-mdi-plus"
        @click="chooseFile"
      />

      <BaseButton
        text="Upload"
        icon-class="i-mdi-upload"
        :disabled="files.length === 0"
        :class="files.length === 0 ? 'cursor-not-allowed opacity-50' : ''"
        @click="upload"
      />

      <BaseButton
        text="Cancel"
        icon-class="i-mdi-close"
        :disabled="files.length === 0"
        :class="files.length === 0 ? 'cursor-not-allowed opacity-50' : ''"
        @click="fileClear"
      />
    </div>

    <div
      class="relative bg-slate-0 text-slate-700 p-[1.125rem] border border-t-0 border-slate-200 rounded-b-lg [&_[role=progressbar]]:absolute [&_[role=progressbar]]:w-full [&_[role=progressbar]]:top-0 [&_[role=progressbar]]:left-0 [&_[role=progressbar]]:h-1"
      @drop="filesDroped"
      @dragover.prevent
    >
      <div v-if="uploadsMetadata.length > 0">
        <!-- progress bar -->
        <div
          role="progressbar"
          aria-valuemin="0"
          aria-valuemax="100"
          class="overflow-hidden relative border-0 h-5 rounded-md bg-slate-100"
        >
          <div
            class="absolute flex items-center justify-center overflow-hidden bg-primary m-0 h-full w-0 border-0 transition-width duration-1000 ease-in-out"
            :style="{ width: getProgress + '% !important' }"
          ></div>
        </div>

        <!-- images -->
        <div
          v-for="file in uploadsMetadata"
          :key="file.name"
          class="flex p-4 mb-2 last:mb-0 border border-slate-200 gap-2 rounded"
        >
          <!-- image preview -->
          <img
            v-if="file.type.includes('image')"
            :src="file.src"
            class="object-cover rounded w-[90px] h-[90px] cursor-pointer"
          >
          <div
            v-else
            class="flex justify-center items-center bg-slate-100 text-slate-700 w-[90px] h-[90px] rounded"
          >
            <span>{{ fileTypeToString(file) }}</span>
          </div>

          <!-- file details -->
          <div>
            <div class="mb-1 break-all">{{ file.name }}</div>
            <div class="mr-2 mb-1">{{ file.size }}</div>
            <BaseBadge v-if="file.progress === 0" variant="warning">Pending</BaseBadge>
            <BaseBadge v-else variant="success">Success</BaseBadge>
          </div>

          <!-- close icon -->
          <span
            class="h-fit p-1 flex justify-center items-center rounded-full border-transparent hover:bg-red-300 transition duration-200 ease-in-out cursor-pointer overflow-hidden select-none"
            @click="deleteFile(file)"
          >
            <span class="i-mdi-close text-error"></span>
          </span>
        </div>
      </div>

      <!-- empty state -->
      <div
        v-else
        class="flex flex-col items-center justify-center gap-2 py-7"
      >
        <p>Drag and drop your files here</p>
      </div>

    </div>
  </div>
</template>

<script lang="ts" setup>
import BaseButton from '../base/BaseButton.vue'
import BaseBadge from '../base/BaseBadge.vue'
import { computed, ref } from 'vue'
import { isEmpty } from 'underscore'
import StorageService from '@/services/StorageService'
import { useNotification } from '@kyvg/vue3-notification'

const props = defineProps({
  storagePath: {
    type: String,
    required: true
  }
})

const { notify } = useNotification()
const fileInput = ref<HTMLInputElement | null>(null)
const files = ref<File[]>([])
const attachments = ref([])
const uploadsMetadata = ref([])
const storageService = new StorageService()
const emit = defineEmits(['onFileUploadCompleted'])

const chooseFile = () => {
  fileInput.value?.click()
}

const formatBytes = (bytes: number, decimals = 2) => {
  if (bytes === 0) return '0 Bytes'
  const k = 1024
  const dm = decimals < 0 ? 0 : decimals
  const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB']
  const i = Math.floor(Math.log(bytes) / Math.log(k))

  return parseFloat((bytes / Math.pow(k, i)).toFixed(dm)) + ' ' + sizes[i]
}

const fileClear = () => {
  files.value = []
  //keep only the files that are uploaded
  uploadsMetadata.value = uploadsMetadata.value.filter((file) => file.progress === 100)
}

const fileSected = ($event) => {
  const data = $event.target.files
  handleFiles(data)
}

const filesDroped = ($event) => {
  $event.preventDefault()
  const data = $event.dataTransfer.files
  handleFiles(data)
}

const getProgress = computed(() => {
  if (uploadsMetadata.value.length === 0) return 0
  const progress = uploadsMetadata.value.map((file) => file.progress)
  return progress.reduce((acc, cur) => acc + cur) / uploadsMetadata.value.length
})

const handleFiles = (data) => {
  if (isEmpty(data)) return

  if (Object.keys(data).length > 0) {
    for (let file of data) {
      files.value.push(file as File)
      uploadsMetadata.value[buildUploadsMetadata(file)]
    }
  } else {
    files.value.push(data as File)
    uploadsMetadata.value[buildUploadsMetadata(data as File)]
  }
}

const buildUploadsMetadata = (file: File) => {
  const metadata = {
    name: file.name,
    rawType: file.type,
    type: file.type.split('/')[0],
    ext: file.name.split('.').slice(-1)[0],
    size: formatBytes(file.size),
    src: URL.createObjectURL(file),
    progress: 0
  }

  // get array index of the items
  const index = uploadsMetadata.value.push(metadata) - 1

  return index
}

const upload = async () => {
  try {
    for (let fileMetadata of uploadsMetadata.value) {
      const file = files.value.find((item) => item.name === fileMetadata.name)
      // upload file
      const uploadTask = await storageService.upload(file, props.storagePath)
      if (uploadTask.state === 'success') {
        fileMetadata.progress = 100
        const url = await storageService.download(uploadTask.ref.fullPath)
        if (url) attachments.value.push({ url, name: file.name })
      }
    }
    files.value = []
    emit('onFileUploadCompleted', attachments.value.map((attachment) => attachment.url))

  } catch (error) {
    console.error(error)
    notify({
      type: 'error',
      title: 'Error',
      text: error.message || 'An error occurred while uploading the file'
    })
  }
}

const deleteFile = async (file) => {
  // remove file from uploadsMetadata
  const index = uploadsMetadata.value.findIndex((item) => item.name === file.name)

  if (uploadsMetadata.value[index].progress === 100) {
    const attachment = attachments.value.find((item) => item.name === file.name)
    try {
      await storageService.deleteAttachmentWithStyledImages(attachment.url)
      attachments.value.splice(attachments.value.indexOf(attachment), 1)
    } catch (error) {
      notify({
        type: 'error',
        title: 'Error',
        text: error.message || 'An error occurred while deleting the file'
      })
      return
    }
  }

  uploadsMetadata.value.splice(index, 1)

  // remove file from files
  files.value = files.value.filter((item) => item.name !== file.name)
}

const fileTypeToString = (fileMetadata) => {
  const map = {
    'vnd.openxmlformats-officedocument.spreadsheetml.sheet': 'xlsx'
  }

  const ext = fileMetadata.ext || map[fileMetadata.rawType.split('/')[1]] || fileMetadata.fileMetadata.rawType.split('/')[1]
  return ext.toUpperCase()
}
</script>
